import React from "react";
import { DatePickerV3 } from "../../../../../components/date-picker-v3";
import { FormErrorText } from "../../../../../components/form";
import { Input } from "../../../../../components/input";
import { Switch } from "../../../../../components/switch";
import { MerchantCouponUIState } from "../../data/merchantCouponEditor.data";
import type { MerchantCouponValidationErrors } from "../../schemas/merchantCouponEditor.schema";
import { UpgradeToUnlockButton } from "./UpgradeToUnlockButton";

export type PlanType = "free" | "basic" | "premium";

interface CouponSettingsSectionProps {
  coupon: MerchantCouponUIState;
  onFieldChange: (field: keyof MerchantCouponUIState, value: any) => void;
  isCreating: boolean;
  planType?: PlanType;
  errors: MerchantCouponValidationErrors;
}

/* Spec from Figma HTML export – align with Coupon Details / Usage Limits */
const labelClass =
  "text-[#75757D] dark:text-gray-400 text-[14px] font-normal font-['Nunito'] leading-[21px]";
const asteriskClass = "text-[#FB2C36] text-[14px] font-normal font-['Nunito'] leading-[21px]";
const labelLockedClass =
  "text-[#CBCED4] dark:text-gray-500 text-[14px] font-normal font-['Nunito'] leading-[21px]";
const dateInputBase =
  "rounded-[8px] border border-[#E2E8F0] dark:border-gray-600 h-[42.6px] px-[15.8px] py-[9.8px] text-[14px] font-['Segoe_UI'] text-[#0A0A0A] dark:text-gray-100 bg-white dark:bg-gray-900 w-full justify-start text-left font-normal flex items-center";
const inputLockedBase = "rounded-[10px] border border-[#CBCED4] dark:border-gray-500";
/* Basic/Premium: Low Stock input uses rounded 10 + #E2E8F0 per Figma */
const inputLowStockUnlocked = "rounded-[10px] border border-[#E2E8F0] dark:border-gray-600";
const inputCommon =
  "bg-white dark:bg-gray-900 text-[14px] font-['Segoe_UI'] text-[#0A0A0A] dark:text-gray-100 placeholder:text-[#0A0A0A]/50 disabled:opacity-60";

export const CouponSettingsSection: React.FC<CouponSettingsSectionProps> = ({
  coupon,
  onFieldChange,
  isCreating,
  planType = "free",
  errors,
}) => {
  const alertLowStockLocked = planType === "free";

  const handleDateChange = (field: "start_date" | "end_date", date: Date | undefined) => {
    onFieldChange(field, date ? date.toISOString() : null);
  };

  const handleLowStockThresholdChange = (value: string) => {
    const normalizedValue = value.replace(/^0+(?=\d)/, "");
    onFieldChange("low_stock_threshold", parseInt(normalizedValue, 10) || 0);
  };

  return (
    <div className="self-stretch p-[23.8px] bg-white dark:bg-gray-800 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-[#E6E6E6] dark:outline-gray-600 flex flex-col gap-6">
      {/* Header */}
      <div className="self-stretch pb-2 border-b-[0.80px] border-[#00C49A] dark:border-emerald-500 flex flex-col">
        <h2 className="text-[#004232] dark:text-emerald-100 text-[18px] font-normal font-['Nunito'] leading-[27px]">
          Coupon Settings
        </h2>
      </div>

      <div className="self-stretch flex flex-col gap-5 items-stretch">
        {/* Row 1: Start Date & End Date – gap 16px */}
        <div className="self-stretch flex flex-col md:flex-row gap-4">
          <div className="flex-1 flex flex-col gap-2">
            <div className="self-stretch">
              <span className={labelClass}>Start Date </span>
              <span className={asteriskClass}>*</span>
            </div>
            <div className="self-stretch">
              <DatePickerV3
                value={coupon.start_date ? new Date(coupon.start_date) : undefined}
                onChange={(date) => handleDateChange("start_date", date)}
                disabled={isCreating}
                placeholder="MM/DD/YYYY"
                dateFormat="MM/dd/yyyy"
                className={dateInputBase}
              />
              <FormErrorText message={errors.start_date} />
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-2">
            <div className="self-stretch">
              <span className={labelClass}>End Date </span>
              <span className={asteriskClass}>*</span>
            </div>
            <div className="self-stretch">
              <DatePickerV3
                value={coupon.end_date ? new Date(coupon.end_date) : undefined}
                onChange={(date) => handleDateChange("end_date", date)}
                disabled={isCreating}
                placeholder="MM/DD/YYYY"
                dateFormat="MM/dd/yyyy"
                disableToDate={
                  coupon.start_date
                    ? new Date(new Date(coupon.start_date).getTime() - 24 * 60 * 60 * 1000)
                    : undefined
                }
                className={dateInputBase}
              />
              <FormErrorText message={errors.end_date} />
            </div>
          </div>
        </div>

        {/* Row 2: Low Stock Threshold & Alert low stock – gap 16px */}
        <div className="self-stretch flex flex-col md:flex-row gap-4 items-start relative">
          <div className="flex-1 flex flex-col gap-2 self-stretch md:self-auto min-w-0">
            <div className="self-stretch">
              <span className={alertLowStockLocked ? labelLockedClass : labelClass}>
                Low Stock Threshold
              </span>
            </div>
            <Input
              type="number"
              value={coupon.low_stock_threshold ?? ""}
              min={0}
              onChange={(e) => handleLowStockThresholdChange(e.target.value)}
              disabled={isCreating || alertLowStockLocked}
              placeholder="10"
              className={`${alertLowStockLocked ? inputLockedBase : inputLowStockUnlocked} ${inputCommon} h-[42.6px] px-[15.8px] py-[11.8px]`}
            />
          </div>

          <div className="flex-1 flex justify-end items-center gap-3 flex-wrap min-w-0">
            <div className="flex items-center gap-3">
              <Switch
                checked={coupon.alert_low_stock}
                onCheckedChange={(checked) => onFieldChange("alert_low_stock", checked)}
                disabled={isCreating || alertLowStockLocked}
                className={`data-[state=unchecked]:bg-[#CBCED4] dark:data-[state=unchecked]:bg-gray-500 data-[state=checked]:bg-[#00C49A] dark:data-[state=checked]:bg-emerald-500 ${alertLowStockLocked ? "opacity-60" : ""}`}
              />
              <span className={alertLowStockLocked ? labelLockedClass : labelClass}>
                Alert low stock
              </span>
            </div>
            {alertLowStockLocked && (
              <UpgradeToUnlockButton
                variant="pill"
                className="shrink-0"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
